import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setOpenAmberCopilot } from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import styles from "./CopilotDock.module.scss";
// Same mark the Live chat row uses. See the note on that import for why it is an asset and not
// the JSX component amber-user-website keeps it as.
import copilotMark from "../../assets/amber-copilot.svg";
import wrapperHOC from "@Utils/wrapperHOC";
import Image from "@Components/Image";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * The amber Copilot input, docked on the hero photo.
 *
 * ── Ported, not invented ────────────────────────────────────────────────────
 * Structure and metrics are amber-user-website's `CopilotContextualInput`: a 1px gradient ring
 * (#ffab00 → #ff5771 → #ff33b1 at 135°) around a 48px white pill, the Copilot mark at 20px on the
 * left, the field, and a 34px round send button that fills to #1f2a37 once there is something to
 * send. Everything here that looks like a specific number is from that component.
 *
 * ── What it does ────────────────────────────────────────────────────────────
 * It opens the real Copilot. Focusing the field, submitting, or pressing the send button all
 * dispatch `setOpenAmberCopilot(true)`, which is the same action the floating Copilot button
 * fires — so this dock is a second entry point to one panel, not a second chat.
 *
 * It was inert before ("no navigation, no request, no console noise"), which read as a broken
 * input: the placeholder types itself, the send button fills in, and then nothing happened.
 *
 * ⚠️  THE TYPED QUERY IS NOT CARRIED THROUGH. `setOpenAmberCopilot` takes a boolean and there is
 * no open-with-message action on the chatbot reducer, so the panel opens on its own greeting and
 * the reader retypes. Threading the query needs a new action there; this only opens the panel.
 */

/**
 * The rotating placeholder, typed out a character at a time.
 *
 * Contact-page questions rather than the search-page ones the original cycles — a reader who has
 * arrived here is asking about their own booking, not browsing.
 */
const SUGGESTION_KEYS = [
  "contact.v2.copilot.suggestion1",
  "contact.v2.copilot.suggestion2",
  "contact.v2.copilot.suggestion3",
  "contact.v2.copilot.suggestion4",
];

/** Milliseconds per character while typing, and while deleting. */
const TYPE_MS = 55;
const DELETE_MS = 28;
/** How long a finished suggestion sits before it starts deleting. */
const HOLD_MS = 1800;

const CopilotDock = () => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  // Resolved up front so the typing loop steps through finished strings rather
  // than calling t() on every character.
  const suggestions = useMemo(() => SUGGESTION_KEYS.map((key) => t(key)), [t]);
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // The same action the floating Copilot button dispatches, so both entry points land on one
  // panel. `AmberCopilotWrapper` on the page is what listens for it.
  const openCopilot = () => {
    dispatch(setOpenAmberCopilot(true));
  };
  /** The suggestion text as typed so far. */
  const [typed, setTyped] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The typing loop.
   *
   * A chain of one-shot timeouts rather than an interval, because the delay changes between
   * phases — typing is slower than deleting, and the hold at the end of a phrase is neither. An
   * interval would need its own state machine to vary that and would drift against it.
   *
   * It stops entirely while the field is focused or has content: the animation is a placeholder,
   * and a placeholder that keeps typing under someone's cursor is just noise competing with them.
   */
  useEffect(() => {
    const still =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isFocused || query || still) {
      if (timer.current) clearTimeout(timer.current);
      // Reduced motion still gets a prompt, just a stationary one.
      if (still && !query) setTyped(suggestions[0]);
      return undefined;
    }

    let phrase = 0;
    let chars = 0;
    let deleting = false;
    let cancelled = false;

    const step = () => {
      if (cancelled) return;
      const full = suggestions[phrase];

      if (!deleting) {
        chars += 1;
        setTyped(full.slice(0, chars));
        if (chars === full.length) {
          deleting = true;
          timer.current = setTimeout(step, HOLD_MS);
          return;
        }
        timer.current = setTimeout(step, TYPE_MS);
        return;
      }

      chars -= 1;
      setTyped(full.slice(0, chars));
      if (chars === 0) {
        deleting = false;
        phrase = (phrase + 1) % suggestions.length;
      }
      timer.current = setTimeout(step, DELETE_MS);
    };

    timer.current = setTimeout(step, TYPE_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [isFocused, query, suggestions]);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        // Prevented so the page does not reload; the panel is what handles the query.
        e.preventDefault();
        openCopilot();
      }}
    >
      <div className={styles.ring}>
        <div className={styles.inner}>
          <span className={styles.icon}>
            <Image src={copilotMark} alt="" width={20} height={20} isNotLazy />
          </span>

          <div className={styles.field}>
            <input
              type="text"
              className={styles.input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              // Opening on focus rather than on click: clicking IS a focus, and this way the
              // keyboard reaches it too — tabbing to the field opens the panel just the same.
              onClick={openCopilot}
              onBlur={() => setIsFocused(false)}
              aria-label={t("contact.v2.copilot.aria")}
            />
            {/* Rendered as our own element rather than the input's `placeholder` attribute, which
                cannot hold a blinking caret — and which would announce the whole rotating string
                to a screen reader as if it were the field's label. The real label is on the input;
                this is decorative. */}
            {!query ? (
              <span className={styles.placeholder} aria-hidden="true">
                {isFocused ? (
                  t("contact.v2.copilot.focusedplaceholder")
                ) : (
                  <>
                    {typed}
                    <span className={styles.caret} />
                  </>
                )}
              </span>
            ) : null}
          </div>

          <button
            type="submit"
            className={`${styles.send} ${query.trim() ? styles.sendActive : ""}`}
            aria-label={t("contact.v2.copilot.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </div>
    </form>
  );
};

export default wrapperHOC(CopilotDock, {
  componentName: "CopilotDock-ContactUsV2Card",
  showForChina: true,
});
